import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ShareModal = ({ isOpen, onClose, dishName, screenshotUrl }) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const shareUrl = window.location?.href;

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    if (!screenshotUrl) return;
    const a = document.createElement('a');
    a.href = screenshotUrl;
    a.download = `${dishName?.replace(/\s+/g, '-') || 'ar-dish'}-screenshot.png`;
    a?.click();
  };

  const shareText = `Check out ${dishName} in AR! 🍽️`;

  const shareOptions = [
    {
      name: 'Facebook',
      icon: 'Facebook',
      color: '#1877F2',
      action: () => window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
        '_blank'
      )
    },
    {
      name: 'WhatsApp',
      icon: 'MessageCircle',
      color: '#25D366',
      action: () => window.open(
        `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`,
        '_blank'
      )
    },
    {
      name: 'Email',
      icon: 'Mail',
      color: '#EA4335',
      action: () => {
        window.location.href = `mailto:?subject=${encodeURIComponent(`Check out ${dishName} in AR!`)}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`;
      }
    },
    {
      name: 'Download',
      icon: 'Download',
      color: '#6366F1',
      action: handleDownload
    }
  ];

  return (
    <div
      className="share-modal-overlay fixed inset-0 bg-background/80 backdrop-blur-sm z-modal flex items-end md:items-center justify-center p-0 md:p-4"
      onClick={onClose}
    >
      <div
        className="share-modal-content bg-card rounded-t-2xl md:rounded-2xl shadow-warm-2xl border border-border w-full md:max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e?.stopPropagation()}
      >
        <div className="share-modal-header sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
            Share AR Experience
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="touch-target transition-smooth active:scale-96"
            iconName="X"
            iconSize={20}
            aria-label="Close share modal"
          >
            <span className="sr-only">Close</span>
          </Button>
        </div>

        <div className="share-modal-body p-4 md:p-6 space-y-6">
          {screenshotUrl && (
            <div className="screenshot-preview">
              <p className="text-sm font-caption text-muted-foreground mb-3">
                Your AR Screenshot
              </p>
              <div className="relative aspect-video rounded-lg overflow-hidden bg-muted">
                <img
                  src={screenshotUrl}
                  alt={`AR view of ${dishName}`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          )}

          <div className="share-options">
            <p className="text-sm font-caption text-muted-foreground mb-3">
              Share via
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {shareOptions?.map((option) => (
                <button
                  key={option?.name}
                  onClick={option?.action}
                  disabled={option?.name === 'Download' && !screenshotUrl}
                  className="share-option-btn flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-smooth active:scale-96 touch-target disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${option?.color}15` }}
                  >
                    <Icon name={option?.icon} size={24} color={option?.color} />
                  </div>
                  <span className="text-xs md:text-sm font-caption text-foreground">
                    {option?.name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="copy-link-section">
            <p className="text-sm font-caption text-muted-foreground mb-3">
              Or copy link
            </p>
            <div className="flex items-center gap-2">
              <div className="flex-1 px-4 py-3 bg-muted rounded-lg border border-border text-sm font-caption text-foreground truncate">
                {shareUrl}
              </div>
              <Button
                variant={copied ? "success" : "outline"}
                onClick={handleCopyLink}
                className="touch-target transition-smooth active:scale-96 flex-shrink-0"
                iconName={copied ? "Check" : "Copy"}
                iconSize={20}
              >
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;